import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Label } from '../../../../components/ui/label';
import { Checkbox } from '../../../../components/ui/checkbox';
import { useToast } from '../../../../hooks/use-toast';
import api from '../../../../services/apiServices';
import { useAdminStore } from '../../../../store/adminStore'; // <-- NEW IMPORT

// These should match your backend Enum. The "categorized" flags need output labels.
const flagTypes = [
    { value: "urgent", label: "Urgent", type: 'boolean' },
    { value: "business_lead", label: "Business Lead", type: 'boolean' },
    { value: "remote_service", label: "Remote Service", type: 'boolean' },
    { value: "language", label: "Language", type: 'value_capture' },
    { value: "budget_tier", label: "Budget Tier", type: 'categorized' },
    { value: "project_scale", label: "Project Scale", type: 'categorized' },
    { value: "quality_risk", label: "Quality Risk (excludes from prospect emailing)", type: 'boolean' },
];

export default function QnAFlagsSetup({ category, questions }) {
    const [flags, setFlags] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const { toast } = useToast();
    const categoryId = category?.id;

    // Form State
    const [selectedFlagType, setSelectedFlagType] = useState('');
    const [selectedQuestionId, setSelectedQuestionId] = useState('');
    const [selectedOptions, setSelectedOptions] = useState(new Set());
    const [availableOutputLabels, setAvailableOutputLabels] = useState([]);
    const [selectedOutputLabel, setSelectedOutputLabel] = useState('');

    const flagTypeDetails = flagTypes.find(f => f.value === selectedFlagType);
    const locale = useAdminStore((state) => state.locale); // <-- GET CONTEXT
    
    // ** MODIFICATION HERE: Filter questions before rendering **
    // Only allow questions with predefined options to be selected for flags.
    const filteredQuestions = questions.filter(
        q => q.question_type === 'single_select' || q.question_type === 'multi_select'
    );
    
    const selectedQuestion = filteredQuestions.find(q => q.id === parseInt(selectedQuestionId));

    const resetForm = useCallback(() => {
        setSelectedFlagType('');
        setSelectedQuestionId('');
        setSelectedOptions(new Set());
        setAvailableOutputLabels([]);
        setSelectedOutputLabel('');
    }, []);
    
    const fetchFlags = useCallback(async () => {
        if (!categoryId) return;
        setIsLoading(true);
        try {
            const res = await api.get(`/admin/catgeory-flags/categories/${categoryId}/qna-flags`,{
                params: { locale },
            });
            setFlags(res.data);
        } catch (error) {
            toast({
            variant: "destructive",
            title: "Error",
            description: error?.response?.data?.message || error.message || "Failed to fetch flag configurations.",
            });
        } finally {
            setIsLoading(false);
        }
        }, [categoryId, toast]);

    useEffect(() => {
        fetchFlags();
    }, [fetchFlags]);

    // Effect to fetch output labels when a categorized flag type is selected
    useEffect(() => {
        if (flagTypeDetails?.type !== 'categorized') {
            setAvailableOutputLabels([]);
            setSelectedOutputLabel('');
            return;
        }

        const fetchOutputLabels = async () => {
            try {
            const res = await api.get(`/admin/catgeory-flags/flag-definitions/by-type/${selectedFlagType}`);
            setAvailableOutputLabels(res.data);
            } catch (error) {
            toast({
                variant: "destructive",
                title: "Error",
                description: error?.response?.data?.message || error.message || "Could not fetch labels for this flag type.",
            });
            }
        };

        fetchOutputLabels();
        }, [selectedFlagType, flagTypeDetails, toast]);


    const handleOptionToggle = (optionId) => {
        const newSelection = new Set(selectedOptions);
        if (newSelection.has(optionId)) {
            newSelection.delete(optionId);
        } else {
            newSelection.add(optionId);
        }
        setSelectedOptions(newSelection);
    };

    const handleAddFlag = async (e) => {
        e.preventDefault();

        if (!selectedFlagType || !selectedQuestionId || selectedOptions.size === 0) {
            toast({ title: "Validation Error", description: "Please complete all fields." });
            return;
        }

        if (flagTypeDetails?.type === 'categorized' && !selectedOutputLabel) {
            toast({ title: "Validation Error", description: "Please select an output label." });
            return;
        }

        const payload = {
            question_id: parseInt(selectedQuestionId),
            flag_type: selectedFlagType,
            trigger_option_ids: Array.from(selectedOptions),
            output_label: flagTypeDetails?.type === 'categorized' ? selectedOutputLabel : null,
        };

        try {
            const res = await api.post(`/admin/catgeory-flags/categories/${categoryId}/qna-flags`, payload,{
                params: { locale },
            });
            const newFlag = res.data;
            setFlags(prev => [...prev, newFlag]);
            toast({ title: "Success", description: "Flag rule saved." });
            resetForm();
        } catch (error) {
            toast({
            variant: "destructive",
            title: "Save Error",
            description: error?.response?.data?.detail || error.message || "Failed to save flag rule.",
            });
        }
        };
    
    const handleDeleteFlag = async (flagId) => {
        if (!window.confirm("Are you sure you want to delete this rule?")) return;

        try {
            await api.delete(`/admin/catgeory-flags/qna-flags/${flagId}`,{
                params: { locale },
            });
            setFlags(prev => prev.filter(f => f.id !== flagId));
            toast({ title: "Success", description: "Flag rule deleted." });
        } catch (error) {
            toast({
            variant: "destructive",
            title: "Delete Error",
            description: error?.response?.data?.message || error.message || "Could not delete the rule.",
            });
        }
    };


    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle>Add New Flag Rule</CardTitle>
                    <CardDescription>Define rules that will add flags to leads based on customer answers.</CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleAddFlag} className="space-y-4">
                        {/* Row 1: Flag Type and Question Selection */}
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <Label>1. Select Flag Type</Label>
                                <Select onValueChange={setSelectedFlagType} value={selectedFlagType}>
                                    <SelectTrigger><SelectValue placeholder="Choose a flag type..." /></SelectTrigger>
                                    <SelectContent>
                                        {flagTypes.map(ft => <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>2. Select Controlling Question</Label>
                                <Select onValueChange={id => { setSelectedQuestionId(id); setSelectedOptions(new Set()); }} value={selectedQuestionId} disabled={!selectedFlagType}>
                                    <SelectTrigger><SelectValue placeholder="Choose a question..." /></SelectTrigger>
                                    <SelectContent>
                                        {/* ** MODIFICATION HERE: Using the filtered list ** */}
                                        {filteredQuestions.map(q => <SelectItem key={q.id} value={q.id.toString()}>{q.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Row 2: Trigger Options */}
                        {selectedQuestion && (
                            <div>
                                <Label>3. Select Trigger Answer(s)</Label>
                                <div className="p-4 border rounded-md max-h-48 overflow-y-auto space-y-2">
                                    {selectedQuestion.options.map(opt => (
                                        <div key={opt.id} className="flex items-center gap-2">
                                            <Checkbox id={`opt-${opt.id}`} checked={selectedOptions.has(opt.id)} onCheckedChange={() => handleOptionToggle(opt.id)} />
                                            <Label htmlFor={`opt-${opt.id}`}>{opt.label}</Label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        {/* Row 3: Output Label (Conditional) */}
                        {flagTypeDetails?.type === 'categorized' && availableOutputLabels.length > 0 && (
                             <div>
                                <Label>4. Select Output Label</Label>
                                <Select onValueChange={setSelectedOutputLabel} value={selectedOutputLabel}>
                                    <SelectTrigger><SelectValue placeholder="Choose an output label..." /></SelectTrigger>
                                    <SelectContent>
                                        {availableOutputLabels.map(label => <SelectItem key={label} value={label}>{label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        <Button type="submit" disabled={!selectedQuestionId || selectedOptions.size === 0}>Save Rule</Button>
                    </form>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Configured Flag Rules</CardTitle>
                </CardHeader>
                <CardContent>
                    {isLoading && <p>Loading...</p>}
                    <div className="space-y-4">
                        {flags.length === 0 && !isLoading && <p className="text-muted-foreground">No flag rules configured for this category.</p>}
                        {flags.map(flag => (
                            <div key={flag.id} className="p-4 border rounded-md flex justify-between items-center">
                                <div>
                                    <p className="font-bold text-lg">{flag.flag_type}
                                        {flag.output_label && <span className="ml-2 px-2 py-0.5 bg-secondary text-secondary-foreground rounded-full text-sm font-normal">{flag.output_label}</span>}
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        When answer to "<strong>{flag.question_label}</strong>" is: 
                                        <span className="ml-1 font-medium text-foreground">{flag.trigger_option_labels.join(' OR ')}</span>
                                    </p>
                                </div>
                                <Button variant="destructive" size="sm" onClick={() => handleDeleteFlag(flag.id)}>Delete</Button>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}